import { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, ArrowRight, Check, Play, X } from 'lucide-react';
import { TourVideo } from './tour-video-slot';
import { PAGE_GUIDE_MEDIA } from './page-guide-media';
import './page-guide.css';

const INSPIRATION_GUIDE_LESSONS = [
  {
    title: 'Explore Inspiration',
    body: 'Open Ad templates to browse the image library and find a direction for your next ad. Competitors and My Ads show available scanned ads; Find ads has its own search tools.',
    tip: 'The clip uses sample data. Your current search, filters, and selected ads stay exactly as you left them.',
    media: 'inspiration-overview',
  },
  {
    title: 'Search for an idea',
    body: 'Use the search field above the filters. Try a brand name, product, or visual idea; the keyword narrows the image results.',
    tip: 'The sample searches for “wellness.” Try fewer words if your own library has no matches.',
    media: 'inspiration-search',
  },
  {
    title: 'Refine your results',
    body: 'Open Add Filter. Niche narrows the industry, Format narrows the ad format, and Style narrows the creative approach, such as Social Proof.',
    tip: 'The sample combines niche, format, and style. You can choose more than one option in a category.',
    media: 'inspiration-filters',
  },
  {
    title: 'Choose images and a brand collection',
    body: 'Choose Images in All media to focus on image ads. All Templates shows the full library; choose a brand in that menu to see images saved to its collection.',
    tip: 'The sample brand collection already has saved images. Your own collection fills as you save templates.',
    media: 'inspiration-collection',
  },
  {
    title: 'Clone an image ad',
    body: 'Choose the eye icon on an image card to inspect it. Select Clone this ad to open the destination picker, where you can review the brand and destination before generating variations.',
    tip: 'The sample stops at the picker. No generation is submitted by this guide.',
    media: 'inspiration-clone',
  },
  {
    title: 'Reset and explore again',
    body: 'Remove one filter with its × button, or choose Reset filters to clear the keyword, filter chips, media type, and collection together. Saved images remain in their brand collections.',
    tip: 'If you see no matches, remove a constraint before starting over. You can replay this page guide whenever you need it.',
    media: 'inspiration-reset',
  },
];

/** Local, read-only education: never changes library or global onboarding state. */
export function InspirationPageGuide() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const nextRef = useRef<HTMLButtonElement>(null);
  const complete = step === INSPIRATION_GUIDE_LESSONS.length;
  const lesson = INSPIRATION_GUIDE_LESSONS[Math.min(step, INSPIRATION_GUIDE_LESSONS.length - 1)];
  const media = PAGE_GUIDE_MEDIA[lesson.media];
  const changeStep = (next: number) => {
    setStep(Math.max(0, Math.min(INSPIRATION_GUIDE_LESSONS.length, next)));
    nextRef.current?.focus();
  };
  return <Dialog.Root open={open} onOpenChange={value => { setOpen(value); if (value) setStep(0); }}>
    <Dialog.Trigger asChild>
      <button className="page-guide-trigger" type="button" data-testid="inspiration-page-guide"><Play size={14} aria-hidden="true" />Page guide</button>
    </Dialog.Trigger>
    <Dialog.Portal>
      <Dialog.Overlay className="page-guide-overlay" />
      <Dialog.Content className="page-guide-dialog" data-testid="page-guide-dialog" onKeyDown={event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowRight' && !complete) { event.preventDefault(); changeStep(step + 1); }
        if (event.key === 'ArrowLeft' && step > 0) { event.preventDefault(); changeStep(step - 1); }
      }}>
        <div className="page-guide-topline">
          <span className="page-guide-number" aria-hidden="true">{complete ? <Check size={16} /> : step + 1}</span>
          <span>Inspiration · Page guide</span>
          <span className="page-guide-count" aria-live="polite" aria-atomic="true">{complete ? 'Complete' : `${step + 1} of ${INSPIRATION_GUIDE_LESSONS.length}`}</span>
          <Dialog.Close asChild><button type="button" className="page-guide-close" aria-label="Close page guide"><X size={18} /></button></Dialog.Close>
        </div>
        <div className="page-guide-body">
          <Dialog.Title className="page-guide-title">{complete ? 'You’re ready to explore.' : lesson.title}</Dialog.Title>
          <Dialog.Description className="page-guide-description">{complete ? 'Find a direction, refine your search, and bring the right creative into your next task. Your Inspiration page is just as you left it.' : lesson.body}</Dialog.Description>
          {!complete && media && <div className="page-guide-media"><TourVideo key={lesson.media} media={media} wide playback="once" /></div>}
          {!complete && <p className="page-guide-tip">{lesson.tip}</p>}
          {complete && <button className="page-guide-replay" type="button" onClick={() => changeStep(0)}>Replay this guide</button>}
        </div>
        <div className="page-guide-footer">
          {step > 0 ? <button type="button" className="page-guide-back" onClick={() => changeStep(step - 1)}><ArrowLeft size={14} />Back</button> : <span />}
          <div className="page-guide-footer-actions">
            {!complete && <Dialog.Close asChild><button type="button" className="page-guide-skip">Skip guide</button></Dialog.Close>}
            <button ref={nextRef} type="button" className="page-guide-next" onClick={() => complete ? setOpen(false) : changeStep(step + 1)}>{complete ? 'Explore inspiration' : step === INSPIRATION_GUIDE_LESSONS.length - 1 ? 'Finish' : 'Next'}{!complete && <ArrowRight size={14} />}</button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
